/* Terra — product page behavior: variant picking (live store data),
   media gallery, sticky add-to-cart bar. */

(() => {
  'use strict';

  /* --- Variant picker -------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataScript = this.querySelector('[data-variant-json]');
      if (!dataScript) return;
      try {
        this.variants = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }
      this.root = this.closest('[data-product-root]') || document;
      this.addEventListener('change', () => this.onOptionChange());
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset[data-option-index], .variant-select[data-option-index]'))
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((group) => {
          if (group.tagName === 'FIELDSET') {
            const checked = group.querySelector('input:checked');
            return checked ? checked.value : null;
          }
          const select = group.querySelector('select') || group;
          return select.value;
        });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === options[index])
      );
      this.updateLabels();
      this.updateBuyButtons(variant);
      if (!variant) return;

      this.root.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: false }));
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      this.root.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: false, detail: { variant } })
      );

      this.refreshRenderedParts(variant);
    }

    updateLabels() {
      this.querySelectorAll('fieldset[data-option-index]').forEach((group) => {
        const value = group.querySelector('input:checked');
        const label = group.querySelector('[data-selected-value]');
        if (value && label) label.textContent = value.value;
      });
    }

    updateBuyButtons(variant) {
      this.root.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        const label = button.querySelector('[data-add-to-cart-text]') || button;
        if (!variant) {
          button.setAttribute('disabled', 'disabled');
          label.textContent = button.dataset.textUnavailable;
        } else if (!variant.available) {
          button.setAttribute('disabled', 'disabled');
          label.textContent = button.dataset.textSoldOut;
        } else {
          button.removeAttribute('disabled');
          label.textContent = button.dataset.textAddToCart;
        }
      });
    }

    async refreshRenderedParts(variant) {
      const sectionId = this.dataset.section;
      const productUrl = this.dataset.url;
      if (!sectionId || !productUrl) return;
      try {
        const response = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
        if (!response.ok) return;
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        doc.querySelectorAll('[data-variant-refresh]').forEach((freshPart) => {
          const key = freshPart.getAttribute('data-variant-refresh');
          const currentPart = this.root.querySelector(`[data-variant-refresh="${key}"]`);
          if (currentPart) currentPart.innerHTML = freshPart.innerHTML;
        });
      } catch (error) {
        /* keep previous price display on network failure */
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* --- Media gallery ------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-media-track]');
      this.originalOrder = this.track ? Array.from(this.track.children) : [];
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.thumb, true));
      });
      const root = this.closest('[data-product-root]');
      if (root) {
        root.addEventListener('variant:change', (event) => {
          const media = event.detail.variant.featured_media;
          if (media) this.featureMedia(String(media.id));
        });
      }
    }

    /* Zet de media van de gekozen variant vooraan als grote (wide) foto;
       de overige foto's volgen in de oorspronkelijke volgorde. */
    featureMedia(mediaId) {
      if (!this.track || !this.originalOrder.length) return;
      const target = this.originalOrder.find((el) => el.dataset.mediaId === mediaId);
      if (!target) return;
      const order = [target].concat(this.originalOrder.filter((el) => el !== target));
      order.forEach((el, index) => {
        el.style.order = '';
        el.classList.toggle('product__media-item--wide', index === 0);
        el.classList.toggle('product__media-item--square', index !== 0);
        this.track.appendChild(el);
      });
      if (window.matchMedia('(max-width: 989px)').matches) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }
      this.markActiveThumb(mediaId);
    }

    setActiveMedia(mediaId, scroll) {
      const target = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!target) return;
      if (scroll && this.track) {
        const offset = target.offsetLeft - this.track.offsetLeft;
        if (window.matchMedia('(max-width: 989px)').matches) {
          this.track.scrollTo({ left: offset, behavior: 'smooth' });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
      this.markActiveThumb(mediaId);
    }

    markActiveThumb(mediaId) {
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.thumb === mediaId);
        thumb.setAttribute('aria-current', thumb.dataset.thumb === mediaId ? 'true' : 'false');
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* --- Sticky add-to-cart bar ------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.anchor);
      if (!anchor || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const below = entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', !entry.isIntersecting && below);
          });
        },
        { threshold: 0 }
      );
      observer.observe(anchor);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* --- Product recommendations (Shopify recommendations endpoint) ------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim().length) {
            this.innerHTML = fresh.innerHTML;
          }
        })
        .catch(() => {
          /* recommendations are progressive enhancement */
        });
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();
